/**
 * Form-control value readers for native Kerf TextField inputs and Kerf Select.
 * A field action can target TextField's label wrapper, so read its inner native
 * control in that case.
 * A signal-free leaf module used across the screens (app.tsx split, HS2-ZB7GJN).
 */

export function elValue(el: Element | null): string {
  if (el === null) return '';
  const inner = el.matches('input, textarea')
    ? el
    : el.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
  return (
    (inner as HTMLInputElement | HTMLTextAreaElement | null)?.value ??
    (el as HTMLElement & { value?: string }).value ??
    ''
  );
}

/** A select's chosen value from its host `.value`. */
export function selectValue(el: Element | null): string {
  return el === null ? '' : ((el as HTMLElement & { value?: string }).value ?? '');
}

export function elChecked(el: Element | null): boolean {
  if (el === null) return false;
  const inner = el.shadowRoot?.querySelector<HTMLInputElement>('input');
  return inner?.checked ?? (el as HTMLElement & { checked?: boolean }).checked ?? false;
}

export function field(root: HTMLElement, name: string): string {
  return elValue(root.querySelector(`[name="${name}"]`)).trim();
}

export function checked(root: HTMLElement, name: string): boolean {
  return elChecked(root.querySelector(`[name="${name}"]`));
}

export function numField(root: HTMLElement, name: string): number | undefined {
  const v = field(root, name);
  return v === '' ? undefined : Number(v);
}

/**
 * Fetch an export at `url` and hand it to the OS share sheet when the browser
 * supports sharing files (mobile Safari/Chrome), otherwise fall back to a normal
 * download (HS2-41ZSXD). A user cancelling the share sheet is not an error and
 * does not then download; other share failures still fall back to a download.
 * Returns false only when neither delivery path can be prepared.
 */
export async function shareOrDownload(url: string, filename: string): Promise<boolean> {
  let blob: Blob;
  try {
    const res = await fetch(url);
    if (!res.ok) return false;
    blob = await res.blob();
  } catch {
    return false;
  }
  const file = new File([blob], filename, { type: blob.type });
  const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
  if (typeof nav.canShare === 'function' && nav.canShare({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: filename });
      return true;
    } catch (error) {
      // The user dismissed the share sheet — don't surprise them with a download.
      if (error instanceof Error && error.name === 'AbortError') return true;
      // A genuine native-share failure should still deliver the requested export.
    }
  }
  try {
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
    return true;
  } catch {
    return false;
  }
}
